import type { DXAReading } from '@dynamika-shared/types/DXAReading';
import { parseDateOnly } from '@dynamika-shared/utils/date';

import type { Label } from '@restore/clinicalTrialContext/clinicalTrialContext.types';

// NOTE: Single tunable constant — REQ-4. Tune from production data before promoting.
export const PREFILL_THRESHOLD = 0.5;

const STOP_TOKENS = new Set(['', '-', 'and']);

const ANATOMY_ALIASES: Record<string, string> = {
  '33': '_third_',
  '1/3': '_third_',
  third: '_third_',
  excl: '_excluding_',
  excluding: '_excluding_',
  incl: '_including_',
  including: '_including_',
  // D7S-572: the DXA application reports the forearm scan site as the bare word
  // "Forearm", whereas Dynamika labels it "Distal 1/3 Radius". They are the same
  // scan site, so expand "forearm" to that label's tokens for a confident match.
  forearm: 'distal _third_ radius',
};

export function normalize(input: string | null | undefined): string[] {
  if (!input) return [];
  return input
    .toLowerCase()
    .replace(/[(),.]/g, ' ')
    .split(/\s+/)
    .map(t => t.trim())
    .filter(t => !STOP_TOKENS.has(t))
    .flatMap(t => (ANATOMY_ALIASES[t] ?? t).split(' '));
}

export function similarity(qTokens: string[], lTokens: string[]): number {
  if (qTokens.length === 0 || lTokens.length === 0) return 0;
  const Q = new Set(qTokens);
  const L = new Set(lTokens);
  let intersection = 0;
  for (const t of L) if (Q.has(t)) intersection += 1;
  const coverage = intersection / L.size;
  const precision = intersection / Q.size;
  const lengthBonus = Math.min(L.size, 5) * 0.005;
  return 0.65 * coverage + 0.25 * precision + lengthBonus;
}

export type PrefillRow = Pick<
  DXAReading,
  'recordId' | 'bodyRegion' | 'bodyProperty'
> & { acquisitionDate?: DXAReading['acquisitionDate'] };

// NOTE: Recency floor — a row with no parseable acquisitionDate sorts LAST in a
// score tie (loses to any dated row) but is never excluded from an uncontested
// label. Date-only values use parseDateOnly to avoid a timezone shift (D7S-528).
function recency(acquisitionDate: string | null | undefined): number {
  if (!acquisitionDate) return Number.NEGATIVE_INFINITY;
  const date = parseDateOnly(acquisitionDate) ?? new Date(acquisitionDate);
  const time = date.getTime();
  return Number.isNaN(time) ? Number.NEGATIVE_INFINITY : time;
}

// NOTE: Structural minimum — accepts both the restore `Label` and the local
// `Label` type defined in `WorkstationResultsTable.tsx` (which omits
// `inputDataType`). The algorithm consults only `id` and `name`.
export type PrefillLabel = Pick<Label, 'id' | 'name'>;

export function assignPrefill(
  rows: PrefillRow[],
  labels: PrefillLabel[],
  threshold: number = PREFILL_THRESHOLD
): Record<string, string> {
  const candidates: {
    rowId: string;
    labelId: string;
    score: number;
    recency: number;
  }[] = [];

  for (const row of rows) {
    if (!row.recordId) continue;
    const q = normalize(`${row.bodyRegion ?? ''} ${row.bodyProperty ?? ''}`);
    if (q.length === 0) continue;

    const rowRecency = recency(row.acquisitionDate);

    for (const label of labels) {
      const l = normalize(label.name);
      if (l.length === 0) continue;

      const score = similarity(q, l);
      if (score >= threshold) {
        candidates.push({
          rowId: row.recordId,
          labelId: label.id,
          score,
          recency: rowRecency,
        });
      }
    }
  }

  // Sort by descending score; break score ties by most recent acquisitionDate
  // (D7-7091). NaN-safe: equal recencies — including two NEGATIVE_INFINITY —
  // return 0 and preserve the stable input order.
  candidates.sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    if (b.recency !== a.recency) return b.recency - a.recency;
    return 0;
  });

  const assignments: Record<string, string> = {};
  const usedRows = new Set<string>();
  const usedLabels = new Set<string>();
  for (const c of candidates) {
    if (usedRows.has(c.rowId) || usedLabels.has(c.labelId)) continue;
    assignments[c.rowId] = c.labelId;
    usedRows.add(c.rowId);
    usedLabels.add(c.labelId);
  }
  return assignments;
}

// NOTE: Picks which acquisition-date group to auto-expand after a pre-fill.
// `WorkstationResultsTable` groups readings into one collapsible table per
// calendar date, keyed by the date's ISO string (UTC midnight). Since the label
// lands on the most recent table (D7-7091). This returns the index of the most
// recent group that received a pre-fill, or -1 if none did. ISO date-key strings
// sort chronologically under a plain lexical compare, so no re-parsing is needed.
export function pickPrefilledGroupIndex(
  entries: ReadonlyArray<
    readonly [string, ReadonlyArray<{ recordId?: string | null }>]
  >,
  assignments: Record<string, string>
): number {
  let best = -1;
  entries.forEach(([dateKey, rows], i) => {
    const hasPrefill = rows.some(
      r => r.recordId != null && assignments[r.recordId] != null
    );
    if (!hasPrefill) return;
    if (best === -1 || dateKey > entries[best][0]) {
      best = i;
    }
  });
  return best;
}
